import { useMemo, type Ref, type RefCallback } from 'react';

/** Merges several refs (callback or object) into one callback ref. */
export function mergeRefs<T>(...refs: Array<Ref<T> | undefined | null>): RefCallback<T> {
  return (value: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') ref(value);
      else (ref as { current: T | null }).current = value;
    }
  };
}

/**
 * `mergeRefs` with a stable identity.
 *
 * A callback ref rebuilt on every render is a *different* ref to React, so React detaches the old one
 * (calling it with `null`) and attaches the new one after every single render. Anything a consumer does
 * from its ref callback — focusing the first item in a panel, arming a focus trap, measuring — therefore
 * runs again on every unrelated re-render. Merging through this hook keeps one identity for as long as
 * the underlying refs are stable, so the callback fires once per mount and once per unmount.
 */
export function useMergedRefs<T>(...refs: Array<Ref<T> | undefined | null>): RefCallback<T> {
  // The rest parameter *is* the dependency list: one entry per incoming ref, in a fixed order.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => mergeRefs<T>(...refs), refs);
}
